import { Entity } from "playcanvas";
import { DeepReadonly } from "@animech/axiom-format";
import { AttacherInstance, AttacherSolver } from "@animech/supermodel";

/**
 * Data storage that can be used as a shared persistent reference.
 * Derived information that is queried frequently can be housed inside.
 * The contents of this class should never mutate.
 */
export class ConstAttacherState {
	protected attacherInstances: AttacherInstance[];
	protected entityAttacherMap: WeakMap<Entity, AttacherInstance[]>;

	constructor() {
		this.attacherInstances = [];
		this.entityAttacherMap = new WeakMap();
	}

	public getAll(): DeepReadonly<AttacherInstance[]> {
		return this.attacherInstances;
	}

	public getByEntity(
		entity: Entity,
	): DeepReadonly<AttacherInstance[]> | undefined {
		return this.entityAttacherMap.get(entity);
	}

	public static copy(attacherInstances: DeepReadonly<AttacherInstance[]>) {
		return AttacherSolver.copyInstances(
			attacherInstances as AttacherInstance[],
		);
	}
}

/**
 * Data storage that can be used as a shared persistent reference.
 * Derived information that is queried frequently can be housed inside.
 * The contents of this class may be mutated freely. An instance of
 * this class can be cast back into the base class to disable mutability.
 */
export class AttacherState extends ConstAttacherState {
	constructor() {
		super();
	}

	public getAll(): AttacherInstance[] {
		return super.getAll() as ReturnType<typeof this.getAll>;
	}

	public getByEntity(entity: Entity): AttacherInstance[] | undefined {
		return super.getByEntity(entity) as ReturnType<typeof this.getByEntity>;
	}

	public update(attacherInstances: AttacherInstance[]): void {
		const newMap = new WeakMap<Entity, AttacherInstance[]>();

		for (const attacherInstance of attacherInstances) {
			let entry: AttacherInstance[] | undefined = newMap.get(
				attacherInstance.entity,
			);

			if (!entry) {
				entry = [];
				newMap.set(attacherInstance.entity, entry);
			}

			entry.push(attacherInstance);
		}

		this.attacherInstances = attacherInstances;
		this.entityAttacherMap = newMap;
	}
}
